import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import {
  getClientFacet,
  getProjectFacet,
  getOefFacet,
  getActivityFacet,
  getBillingFacet,
  getProgramFacet
} from 'modules/notes/common/components/SearchFacets';
import { SearchFacetType } from 'modules/notes/common/components/SearchFacets';
import { LabelType } from 'graphql/types';
import { useLabels } from 'modules/common/hooks';
import { LabelValueType } from 'modules/notes/common/components/SearchFacets/LabelFacetDetails';
import { useSupervisorInsightContext } from 'modules/Insights/Notes/SupervisorInsightContextProvider';

export const useSearchFacetsInsights = (
  selectedValue: LabelValueType[],
  setSelectedValue: React.Dispatch<React.SetStateAction<LabelValueType[]>>
): SearchFacetType[] => {
  const { timesheetLabels } = useLabels(DateTime.local());
  const { searchLabelType, setSearchLabelType } = useSupervisorInsightContext();

  const onDropdownValueChange = useCallback(
    (mappedValue, label) => {
      setSearchLabelType((previous) => {
        if (previous === '' && mappedValue.length > 0) {
          setSelectedValue(mappedValue);
          return label.name;
        } else if (previous === label.name && mappedValue.length === 0) {
          setSelectedValue([]);
          return '';
        } else {
          setSelectedValue(mappedValue);
          return previous;
        }
      });
    },
    [setSearchLabelType, setSelectedValue]
  );

  const isDisable = useCallback(
    (label) => {
      if (searchLabelType !== '' && searchLabelType !== label.name) {
        return true;
      }
      return false;
    },
    [searchLabelType]
  );

  const settingInitialDopDownValue = useCallback(
    (label, setSelected) => {
      if (searchLabelType !== '' && searchLabelType === label.name) {
        setSelected(selectedValue);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchLabelType]
  );

  return useMemo(() => {
    const facets = [];
    const clientLabel = timesheetLabels.find(
      ({ type }) => type === LabelType.Client
    );
    const programLabel = timesheetLabels.find(
      ({ type }) => type === LabelType.Program
    );
    const projectLabel = timesheetLabels.find(
      ({ type }) => type === LabelType.Project
    );
    const billingLabel = timesheetLabels.find(
      ({ type }) => type === LabelType.Billing
    );
    const activityLabel = timesheetLabels.find(
      ({ type }) => type === LabelType.Activity
    );

    const oefFacets = timesheetLabels
      .filter((label) => label.type === LabelType.Select)
      .map((label) =>
        getOefFacet(
          label,
          onDropdownValueChange,
          isDisable,
          settingInitialDopDownValue
        )
      ) as SearchFacetType[];
    if (clientLabel) {
      facets.push(
        getClientFacet(
          clientLabel,
          onDropdownValueChange,
          isDisable,
          settingInitialDopDownValue
        )
      );
    }
    if (programLabel) {
      facets.push(
        getProgramFacet(
          programLabel,
          onDropdownValueChange,
          isDisable,
          settingInitialDopDownValue
        )
      );
    }
    if (projectLabel) {
      facets.push(
        getProjectFacet(
          projectLabel,
          onDropdownValueChange,
          isDisable,
          settingInitialDopDownValue
        )
      );
    }
    if (billingLabel) {
      facets.push(
        getBillingFacet(
          billingLabel,
          onDropdownValueChange,
          isDisable,
          settingInitialDopDownValue
        )
      );
    }
    if (activityLabel) {
      facets.push(
        getActivityFacet(
          activityLabel,
          onDropdownValueChange,
          isDisable,
          settingInitialDopDownValue
        )
      );
    }

    return [...facets, ...oefFacets];
  }, [
    isDisable,
    onDropdownValueChange,
    settingInitialDopDownValue,
    timesheetLabels
  ]);
};
